<script setup>
import { RecaptchaV2, useRecaptcha } from 'vue3-recaptcha-v2'

const { handleReset } = useRecaptcha()
const { $store } = useNuxtApp()

const metadata = useMetadata()

const inMobileApp = computed(() => $store.getters.inMobileApp)

let recaptchaResponse = null
let recaptchaWidgetId = null

const recaptchaKey = ref(0)
const retryAttempt = ref(0)
const MAX_RETRIES = 3
const RETRY_DELAY_MS = 1000
const TIMEOUT_FOR_WIDGET_ID_MS = 7000
let widgetIdTimeout = null

const handleWidgetId = (widgetId) => {
  recaptchaWidgetId = widgetId
  if (widgetIdTimeout) clearTimeout(widgetIdTimeout)
  retryAttempt.value = 0
}

const storeRecaptcha = (response) => {
  recaptchaResponse = response
}

const recaptchaActive = computed(() => {
  return (
    metadata.constance.USE_RECAPTCHA_REGISTRATION === 'True' &&
    !inMobileApp.value
  )
})

const getRecaptchaResponse = () => {
  return recaptchaResponse
}

async function resetRecaptcha() {
  if (recaptchaActive.value && recaptchaWidgetId) {
    try {
      handleReset(recaptchaWidgetId)
      recaptchaResponse = null
    } catch (e) {
      console.error('resetRecaptcha.error', e)
    }
  } else if (recaptchaActive.value && !recaptchaWidgetId) {
    retryAttempt.value = 0
    triggerRetry()
  }
}

const triggerRetry = () => {
  if (retryAttempt.value < MAX_RETRIES) {
    retryAttempt.value++

    recaptchaWidgetId = null
    recaptchaResponse = null
    if (widgetIdTimeout) clearTimeout(widgetIdTimeout)

    setTimeout(() => {
      recaptchaKey.value++
    }, RETRY_DELAY_MS)
  } else {
    console.error('Max retries reached. RecaptchaV2 failed to initialize.')
  }
}

const handleRecaptchaError = (error) => {
  console.error('RecaptchaV2 error-callback:', error)
  if (widgetIdTimeout) clearTimeout(widgetIdTimeout)
  triggerRetry()
}

const setupWidgetIdTimeout = () => {
  if (widgetIdTimeout) clearTimeout(widgetIdTimeout)

  if (recaptchaActive.value) {
    widgetIdTimeout = setTimeout(() => {
      if (!recaptchaWidgetId) {
        console.warn('Timed out waiting for widget ID.')
        triggerRetry()
      }
    }, TIMEOUT_FOR_WIDGET_ID_MS)
  }
}

onMounted(() => {
  if (recaptchaActive.value) {
    setupWidgetIdTimeout()
  }
})

watch(recaptchaKey, async () => {
  await nextTick()
  if (recaptchaActive.value) {
    setupWidgetIdTimeout()
  }
})

onBeforeUnmount(() => {
  if (widgetIdTimeout) clearTimeout(widgetIdTimeout)
  if (recaptchaActive.value && recaptchaWidgetId) {
    const recap = document.querySelector('.grecaptcha-badge')
    recap?.parentNode?.removeChild(recap)
    resetRecaptcha()
  }
})

defineExpose({
  getRecaptchaResponse,
  resetRecaptcha,
})
</script>

<template>
  <div v-if="recaptchaActive" class="g-recaptcha">
    <RecaptchaV2
      :key="recaptchaKey"
      @widgetId="handleWidgetId"
      @expired-callback="resetRecaptcha"
      @load-callback="storeRecaptcha"
      @error-callback="handleRecaptchaError"
    />
  </div>
</template>
